import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {catchError, forkJoin, Observable, of, tap} from 'rxjs';
import {HttpService} from 'src/app/services/http.service';
import {EPDSFile} from "../models/file-upload";
import {UtilService} from "./util.service";
import {FileUploadComponent} from "../components/file-upload/file-upload.component";


@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  readonly  udmPdfExcel =  'Only PDF & Excel files can be attached';
  readonly  rftPdfExcel = ['pdf', 'xls', 'xlt', 'xlm', 'xlsx', 'xltx', 'xltm', 'xlsb', 'xla', 'xlam', 'xll', 'xlw', 'xlsm'];

  readonly  udmPdfWordExcelZip = 'Only PDF, Word, Excel & Zip files can be attached';
  readonly  rftPdfWordExcelZip = ['pdf', 'xls', 'xlt', 'xlm', 'xlsx', 'xltx', 'xltm', 'xlsb', 'xla', 'xlam', 'xll', 'xlw', 'xlsm', 'docx', 'docm', 'dotx', 'dotm', 'docb', 'doc', 'dot', 'zip', 'zipx'];

  readonly  udmPdfWordExcel = 'Only PDF, Word & Excel can be attached';
  readonly  rftPdfWordExcel = ['pdf', 'xls', 'xlt', 'xlm', 'xlsx', 'xltx', 'xltm', 'xlsb', 'xla', 'xlam', 'xll', 'xlw', 'xlsm', 'docx', 'docm', 'dotx', 'dotm', 'docb', 'doc', 'dot'];

  chunkSize: number = 5000000;

  constructor(private httpService: HttpService, private utilService: UtilService)  { }

  generateUniqueIdentifier() {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let guid = '';
    for (let i = 0; i < 4; i++) {
      guid += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return guid;
  }

  chunkItems(file: File, chunkSize: number) {
    const length = Math.ceil(file.size / chunkSize);
    const chunks = new Array(length).fill(0);
    return chunks.map((_, index) => file.slice(index * chunkSize, (index + 1) * chunkSize));
  }

  submitFile(fileUploadList: { epdsFile: EPDSFile, attachmentType: string }[], a_No: string, fileUpload: FileUploadComponent): Observable<any> {
    const uploadObservables: Observable<any>[] = [];

    if (fileUploadList) {
      for (let aFile of Array.from(fileUploadList)) {
        if (aFile.epdsFile.isFileComplete) continue;

        const flowIdentifier = this.generateUniqueIdentifier();
        const chunks = this.chunkItems(aFile.epdsFile.file, this.chunkSize);

        for (const chunk of chunks) {
          const form = new FormData();
          form.append("attachmentType", aFile.attachmentType);
          form.append("fileIdentifierCode", aFile.epdsFile.typeOfFile);
          form.append("a_No", a_No);
          form.append("flowChunkNumber", (chunks.indexOf(chunk) + 1).toString());
          form.append("flowChunkSize", this.chunkSize.toString());
          form.append("flowCurrentChunkSize", chunk.size.toString());
          form.append("flowTotalSize", aFile.epdsFile.file.size.toString());
          form.append("flowIdentifier", flowIdentifier);
          form.append("flowFilename", aFile.epdsFile.file.name);
          form.append("flowRelativePath", aFile.epdsFile.file.name);
          form.append("flowTotalChunks", chunks.length.toString());
          form.append("file", chunk);
          const headers = new HttpHeaders({'Content-Type': 'application/json'});
          const options = {...headers,responseType: 'text' as const}

          const upload$ = this.httpService.Post<string>('/upload-epds-documents', form, options)
            .pipe(
              tap(item => {
                const flowChunkNumber = +form.get('flowChunkNumber')!;
                const flowTotalChunks = +form.get('flowTotalChunks')!;
                aFile.epdsFile.progress = flowChunkNumber/flowTotalChunks * 100;
                aFile.epdsFile.isFileComplete = (flowChunkNumber === flowTotalChunks);
                console.debug(aFile);
              }),
              catchError(err => {
                console.error("Upload Err", err);
                return of({ isError: true, error: err, file: aFile.epdsFile });
              })
          );

          uploadObservables.push(upload$);
        }
      }
    }

    if (uploadObservables.length == 0) {
      return of("All files already uploaded");
    }

    let that = this;
    return new Observable(function subscribe(subscriber) {
      forkJoin(uploadObservables).subscribe({
        next: (results) => {
          let errMsg = "";
          for (let res of Array.from(results)) {
            if (res && res.isError) {
              if (res.file.typeOfFile === 'P') {
                fileUpload.fileCancel('P');
              } else {
                let idx = fileUpload.associateDataSource.data.findIndex(file => file.name === res.file.name);
                fileUpload.fileCancel('A', false, idx);
              }
              let err = JSON.parse(res.error)
              errMsg += err.error.fileName + ":<br>";
              errMsg += err.error.fileError + "<br><br>";
            }
          }
          if (errMsg.length > 0) {
            that.utilService.displayDialogMessage("Error",
              "File name(s) listed will be removed from the queue. Please fix the issue(s) and upload again.<br><br>" + errMsg);

            subscriber.error(errMsg);
          } else {
            subscriber.next();
          }
          subscriber.complete();
        }
      });
    });

  }

}
